import React from 'react';
import { CheckCircleIcon, ArrowRightIcon, CreditCardIcon, CalendarIcon, ZapIcon } from 'lucide-react';
import { useModal } from '../App';
interface FinancingPlan {
  id: number;
  icon: React.ElementType;
  badge: string;
  title: string;
  monthly: string;
  subtitle: string;
  highlights: string[];
  cta: string;
  featured: boolean;
}
const plans: FinancingPlan[] = [{
  id: 1,
  icon: CalendarIcon,
  badge: 'Most Popular',
  title: '12 Months No Interest',
  monthly: '$0 interest',
  subtitle: 'Pay over 12 months with zero interest charges on approved credit.',
  highlights: ['No interest if paid in full within 12 months', 'Low minimum monthly payments', 'Fast same-day approval decision', 'No prepayment penalties'],
  cta: 'Apply in 60 Seconds',
  featured: true
}, {
  id: 2,
  icon: CreditCardIcon,
  badge: 'Flexible',
  title: 'Low Monthly Payments',
  monthly: 'From $89/mo',
  subtitle: 'Spread your investment over 36–84 months with competitive rates.',
  highlights: ['Terms from 36 to 84 months', 'Rates as low as 6.99% APR', 'Loan amounts up to $75,000', 'No home equity required'],
  cta: 'Check My Rate',
  featured: false
}, {
  id: 3,
  icon: ZapIcon,
  badge: 'Fastest',
  title: 'Same-Day Approval',
  monthly: '2-minute decision',
  subtitle: 'Get approved instantly online — no hard credit pull to check your rate.',
  highlights: ['Soft credit check — no score impact', 'Instant rate quote online', 'Multiple lenders in one application', 'Available 24/7'],
  cta: 'Get Pre-Qualified',
  featured: false
}];
export function Financing() {
  const {
    openModal
  } = useModal();
  return <section className="py-20 md:py-28" style={{
    background: '#F8FAFC'
  }} data-id="element-76">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-77">
        <div className="max-w-2xl mb-14" data-id="element-78">
          <p className="uppercase tracking-widest font-semibold mb-3" style={{
          color: '#F59E0B',
          fontSize: '11px',
          letterSpacing: '0.15em'
        }} data-id="element-79">
            Financing Available
          </p>
          <h2 className="font-black leading-tight mb-4" style={{
          fontSize: 'clamp(36px, 5vw, 56px)',
          color: '#0F172A',
          letterSpacing: '-0.02em'
        }} data-id="element-80">
            A New Roof Shouldn't<br data-id="element-81" />Break the Bank
          </h2>
          <p style={{
          color: '#64748B',
          fontSize: '17px',
          lineHeight: '1.6'
        }} data-id="element-82">
            We offer flexible financing so you can protect your home today — and pay on a schedule that works for you.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-83">
          {plans.map(plan => {
          const Icon = plan.icon;
          return <div key={plan.id} className="rounded-2xl flex flex-col" style={{
            background: plan.featured ? '#0F172A' : '#FFFFFF',
            border: plan.featured ? '2px solid #F59E0B' : '1.5px solid #E2E8F0',
            padding: '28px',
            position: 'relative',
            overflow: 'hidden'
          }} data-id="element-84">
                {plan.featured && <div style={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'rgba(245,158,11,0.06)',
              pointerEvents: 'none'
            }} data-id="element-85" />}
                <div className="flex items-center justify-between mb-5" data-id="element-86">
                  <div className="inline-flex items-center px-3 py-1 rounded-full" style={{
                background: plan.featured ? 'rgba(245,158,11,0.15)' : '#F1F5F9',
                fontSize: '11px',
                fontWeight: 600,
                color: plan.featured ? '#F59E0B' : '#64748B',
                letterSpacing: '0.05em'
              }} data-id="element-87">
                    {plan.badge}
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{
                background: plan.featured ? 'rgba(245,158,11,0.15)' : '#F1F5F9'
              }} data-id="element-88">
                    <Icon size={17} color={plan.featured ? '#F59E0B' : '#94A3B8'} strokeWidth={2} data-id="element-89" />
                  </div>
                </div>
                <h3 className="font-bold mb-1 leading-snug" style={{
              fontSize: '20px',
              color: plan.featured ? '#FFFFFF' : '#0F172A'
            }} data-id="element-90">{plan.title}</h3>
                <p className="font-black mb-3" style={{
              fontSize: '28px',
              color: plan.featured ? '#F59E0B' : '#0F172A',
              letterSpacing: '-0.02em'
            }} data-id="element-91">{plan.monthly}</p>
                <p className="mb-6" style={{
              fontSize: '14px',
              color: plan.featured ? '#94A3B8' : '#64748B',
              lineHeight: '1.6'
            }} data-id="element-92">{plan.subtitle}</p>
                <div className="mb-5" style={{
              height: '1px',
              background: plan.featured ? '#1E293B' : '#F1F5F9'
            }} data-id="element-93" />
                <ul className="flex flex-col gap-2.5 mb-8 flex-1" data-id="element-94">
                  {plan.highlights.map(item => <li key={item} className="flex items-start gap-2.5" data-id="element-95">
                      <CheckCircleIcon size={15} color={plan.featured ? '#F59E0B' : '#94A3B8'} strokeWidth={2} className="mt-0.5 flex-shrink-0" data-id="element-96" />
                      <span style={{
                  fontSize: '13px',
                  color: plan.featured ? '#CBD5E1' : '#64748B',
                  lineHeight: '1.5'
                }} data-id="element-97">{item}</span>
                    </li>)}
                </ul>
                {/* CTA now opens modal */}
                <button onClick={openModal} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold transition-all" style={{
              background: plan.featured ? '#F59E0B' : 'transparent',
              border: plan.featured ? 'none' : '1.5px solid #E2E8F0',
              color: plan.featured ? '#0F172A' : '#475569',
              fontSize: '14px',
              cursor: 'pointer'
            }} onMouseEnter={e => {
              const btn = e.currentTarget;
              if (plan.featured) {
                btn.style.background = '#D97706';
              } else {
                btn.style.borderColor = '#F59E0B';
                btn.style.color = '#F59E0B';
              }
            }} onMouseLeave={e => {
              const btn = e.currentTarget;
              if (plan.featured) {
                btn.style.background = '#F59E0B';
              } else {
                btn.style.borderColor = '#E2E8F0';
                btn.style.color = '#475569';
              }
            }} data-id="element-98">
                  {plan.cta}
                  <ArrowRightIcon size={14} strokeWidth={2.5} data-id="element-99" />
                </button>
              </div>;
        })}
        </div>

        <p className="text-center" style={{
        color: '#94A3B8',
        fontSize: '12px'
      }} data-id="element-100">
          Subject to credit approval. Rates and terms vary by lender and applicant qualifications. Financing provided through third-party lenders. ProRoof is not a lender.
        </p>
      </div>
    </section>;
}